import React, { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { selectCompanyData } from '../../../../../../redux/selector/system'
import AutocompleteInput from '../../../../inputs/AutocompleteInput'
import TextInput from '../../../../inputs/TextInput'
import CheckBoxInputControlled from '../../../../inputs/CheckboxInput/controlled'
import { setCustomersByCompanyName, setCustomersByEmail, setCustomersByName, setCustomersByNumber, setCustomersByPhone } from '../../../../../../redux/action/customer'
import { searchCustomersByCompanyNameThunk, searchCustomersByEmailThunk, searchCustomersByNameThunk, searchCustomersByNumberThunk, searchCustomersByPhoneThunk } from '../../../../../../redux/thunk/customer'
import { setAppEventZIndex } from '../../../../../../redux/action/system'
import { CompanyNameAutocompleteItem, EmailAutocompleteItem, NameAutocompleteItem, NumberAutocompleteItem, PhoneAutocompleteItem } from '../../../../inputs/AutocompleteInput/items'
import { emailPattern } from '../../../../../../helper/email'
import { filterCompanyName, filterName, filterNumber, filterPhone, formatAddressInInput } from '../../../../form/CustomerModalForm/helper'
import { CustomerModalFormAddressWrapper, CustomerModalFormEmailWrapper, CustomerModalFormLine, CustomerModalFormNameContainer, CustomerModalFormWrapper } from '../../../../form/CustomerModalForm/index.styled'
import AddressFields from '../../../../form/AddressFields'
import InvoiceBillingAddress from '../billingAddress'

const PHONE_EVENT_Z_INDEX = 73
const NUMBER_EVENT_Z_INDEX = 74
const NAME_EVENT_Z_INDEX = 75
const EMAIL_EVENT_Z_INDEX = 76
const COMPANY_NAME_EVENT_Z_INDEX = 90

const MainCustomerDataForm = ({ zIndex, submitRecipient, handleSubmit, register, errors, handleEscape, setValue, watch, setFocus, clearErrors, captureCustomerData, paidOrderMode, isBillingAddressSeparate, toggleBillingAddressSeparate, customer, selectCustomerSuggestion, isFirma, toggleFirma }) => {
	const { t } = useTranslation()
	const dispatch = useDispatch()
	const customersByPhone = useSelector(store => store.customerStore.customersByPhone)
	const customersByNumber = useSelector(store => store.customerStore.customersByNumber)
	const customersByName = useSelector(store => store.customerStore.customersByName)
	const customersByEmail = useSelector(store => store.customerStore.customersByEmail)
	const customersByCompanyName = useSelector(store => store.customerStore.customersByCompanyName)

	const companyCountryCode = useSelector(selectCompanyData('countryCode'))
	const watchPhone = watch('phoneNumber')
	const isNameRequired = !paidOrderMode || !isFirma

	const onAutoCompletePhone = useCallback(value => {
		if (value) {
			dispatch(searchCustomersByPhoneThunk(value))
		} else {
			dispatch(setCustomersByPhone(null))
		}
	}, [dispatch])

	const setItemsByPhone = useCallback(value => {
		dispatch(setCustomersByPhone(value))
	}, [dispatch])

	const onAutoCompleteCustomerNumber = useCallback(value => {
		if (value) {
			dispatch(searchCustomersByNumberThunk(value))
		} else {
			dispatch(setCustomersByNumber(null))
		}
	}, [dispatch])

	const setItemsByNumber = useCallback(value => {
		dispatch(setCustomersByNumber(value))
	}, [dispatch])

	const onAutoCompleteCustomerName = useCallback(value => {
		if (value && !watchPhone) {
			dispatch(searchCustomersByNameThunk(value))
		} else {
			dispatch(setCustomersByName(null))
		}
	}, [watchPhone, dispatch])

	const setItemsByName = useCallback(value => {
		dispatch(setCustomersByName(value))
	}, [dispatch])

	const onAutoCompleteCustomerEmail = useCallback(value => {
		if (value && !watchPhone) {
			dispatch(searchCustomersByEmailThunk(value))
		} else {
			dispatch(setCustomersByEmail(null))
		}
	}, [watchPhone, dispatch])

	const setItemsByEmail = useCallback(value => {
		dispatch(setCustomersByEmail(value))
	}, [dispatch])

	const onAutoCompleteCompanyName = useCallback(value => {
		if (value && isFirma && !watchPhone) {
			dispatch(searchCustomersByCompanyNameThunk(value))
		} else {
			dispatch(setCustomersByCompanyName(null))
		}
	}, [dispatch, isFirma, watchPhone])

	const setItemsByCompanyName = useCallback(value => {
		dispatch(setCustomersByCompanyName(value))
	}, [dispatch])

	const onSubmitData = useCallback((data) => {
		submitRecipient(data)
	}, [submitRecipient])

	const registerCustomerAddress = useMemo(() => isBillingAddressSeparate
		? (name, options) => register(name, { ...options, required: false, validate: undefined })
		: register, [isBillingAddressSeparate, register])

	const handleF2 = useCallback(() => {
		handleSubmit(onSubmitData)()
	}, [onSubmitData, handleSubmit])

	const handleCustomerClick = useCallback((data) => {
		selectCustomerSuggestion(data)
		dispatch(setAppEventZIndex(zIndex))
	}, [selectCustomerSuggestion, dispatch, zIndex])

	return (
		<CustomerModalFormWrapper id="invoice-customer-data-form"
															data-testid="invoice-customer-data-form"
															onSubmit={handleSubmit(onSubmitData)}>
			<CustomerModalFormLine>
				<AutocompleteInput testId="customer-phone-number"
													 name="phoneNumber"
													 tabIndex="901"
													 zIndex={PHONE_EVENT_Z_INDEX}
													 returnZIndex={zIndex}
													 label={t('AddOrder.Client.form.Telefonnumer') + '*'}
													 errors={errors}
													 register={register}
													 options={{
														 required: t('app.validation.required'),
														 pattern: {
															 value: /^[0-9]{3,15}$/i,
															 message: t('app.validation.minNumber3'),
														 },
													 }}
													 items={customersByPhone}
													 Item={PhoneAutocompleteItem}
													 watch={watch('phoneNumber')}
													 onAutoComplete={onAutoCompletePhone}
													 filter={filterPhone}
													 setItems={setItemsByPhone}
													 onClick={handleCustomerClick}
													 onReset={handleEscape}
													 onSubmit={handleF2}
													 autoFocus={true}
													 color="gray"
													 inputMode="tel"
													 disabled={captureCustomerData} />
				{paidOrderMode
					? <TextInput testId="customer-number"
											 name="customerNumber"
											 tabIndex="902"
											 label={t('AddOrder.Client.form.customerNumber')}
											 errors={errors}
											 register={register}
											 watch={watch('customerNumber')}
											 color="gray"
											 disabled />
					: <AutocompleteInput testId="customer-number"
															 name="customerNumber"
															 tabIndex="902"
															 disabled={customer ? customer.customerNumber : captureCustomerData}
															 zIndex={NUMBER_EVENT_Z_INDEX}
															 returnZIndex={zIndex}
															 label={t('AddOrder.Client.form.customerNumber')}
															 errors={errors}
															 register={register}
															 options={{
																 pattern: {
																	 value: /^[0-9]{1,150}$/i,
																	 message: t('app.validation.number'),
																 },
															 }}
															 items={customersByNumber}
															 Item={NumberAutocompleteItem}
															 watch={watch('customerNumber')}
															 onAutoComplete={onAutoCompleteCustomerNumber}
															 filter={filterNumber}
															 setItems={setItemsByNumber}
															 onClick={handleCustomerClick}
															 onReset={handleEscape}
															 onSubmit={handleF2}
															 color="gray"
															 inputMode="tel" />}
			</CustomerModalFormLine>
			<CustomerModalFormNameContainer className={isFirma ? 'columns' : ''}>
				<AutocompleteInput testId="customer-name"
													 name="name"
													 tabIndex="903"
													 zIndex={NAME_EVENT_Z_INDEX}
													 returnZIndex={zIndex}
													 label={t('AddOrder.Client.form.Name') + (isNameRequired ? '*' : '')}
													 errors={errors}
													 register={register}
													 options={{
														 required: isNameRequired && t('app.validation.required'),
													 }}
													 items={customersByName}
													 Item={NameAutocompleteItem}
													 watch={watch}
													 onAutoComplete={onAutoCompleteCustomerName}
													 filter={filterName}
													 setItems={setItemsByName}
													 onClick={handleCustomerClick}
													 onReset={handleEscape}
													 onSubmit={handleF2}
													 color="gray"
													 disabled={captureCustomerData}>
					<CheckBoxInputControlled id="firma-id" testId="firma-id" label={t('AddOrder.Client.form.Company')} className="firma-id"
																	 checked={isFirma} onChange={toggleFirma} />
				</AutocompleteInput>
			</CustomerModalFormNameContainer>
			{isFirma &&
				<AutocompleteInput testId="customer-company"
													 name="companyName"
													 tabIndex="904"
													 zIndex={COMPANY_NAME_EVENT_Z_INDEX}
													 returnZIndex={zIndex}
													 label={t('AddOrder.Client.form.Company') + '*'}
													 errors={errors}
													 register={register}
													 options={{
														 required: t('app.validation.required'),
													 }}
													 items={customersByCompanyName}
													 Item={CompanyNameAutocompleteItem}
													 watch={watch('companyName')}
													 onAutoComplete={onAutoCompleteCompanyName}
													 filter={filterCompanyName}
													 setItems={setItemsByCompanyName}
													 onClick={handleCustomerClick}
													 onReset={handleEscape}
													 onSubmit={handleF2}
													 color="gray"
													 disabled={customer && customer?.companyName} />}
			<CustomerModalFormAddressWrapper>
				<AddressFields dataName="customerAddress"
											 firstTabIndex="905"
											 secondTabIndex="906"
											 thirdTbaIndex="907"
											 fourthTabIndex="908"
											 errors={errors}
											 watch={watch}
											 setValue={setValue}
											 setFocus={setFocus}
											 formatAddress={formatAddressInInput}
											 countryDataRequired={false}
											 geoDataRequired={false}
											 autocompleteRequest={true}
											 register={registerCustomerAddress}
											 countryCode={companyCountryCode}
											 returnZIndex={zIndex}
											 cols="cols-2"
											 color="gray"
											 clearErrors={clearErrors}
											 disabled={captureCustomerData || isBillingAddressSeparate} />
			</CustomerModalFormAddressWrapper>

			{customer?.customerNumber &&
				<InvoiceBillingAddress {...{ register, errors, watch, setValue, setFocus, clearErrors, zIndex }}
															 isSeparate={isBillingAddressSeparate}
															 toggleSeparate={toggleBillingAddressSeparate}
															 isFromCustomerCard={!!customer.billingAddress} />}

			<CustomerModalFormEmailWrapper>
				<AutocompleteInput testId="customer-email"
													 name="email"
													 tabIndex="907"
													 zIndex={EMAIL_EVENT_Z_INDEX}
													 returnZIndex={zIndex}
													 label={t('AddOrder.Client.form.email') + '*'}
													 errors={errors}
													 register={register}
													 options={{
														 required: t('app.validation.required'),
														 pattern: {
															 value: emailPattern,
															 message: t('app.validation.invalid_email'),
														 },
													 }}
													 items={customersByEmail}
													 Item={EmailAutocompleteItem}
													 watch={watch}
													 onAutoComplete={onAutoCompleteCustomerEmail}
													 setItems={setItemsByEmail}
													 onClick={handleCustomerClick}
													 onReset={handleEscape}
													 onSubmit={handleF2}
													 color="gray" />
			</CustomerModalFormEmailWrapper>
			<input data-testid="customer-remark" type="hidden" {...register('remark')} />
			<input data-testid="customer-delivery-cost" type="hidden" {...register('deliveryCost')} />
		</CustomerModalFormWrapper>
	)
}

MainCustomerDataForm.displayName = 'MainCustomerDataForm'
export default MainCustomerDataForm
